import { HttpParams } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { catchError, map, Observable, of, startWith } from 'rxjs';
import { ProductionKeyPerformanceIndicatorEvaluationWithData } from 'src/app/models/production-key-performance-indicator-evaluation.model';
import { ApiService } from 'src/app/services/api.service';
import { KpiEvaluationCardComponent } from '../kpi-evaluation-card/kpi-evaluation-card.component';

export type KpiEvaluationPreviewDialogData =
  | { kind: 'data'; evaluation: ProductionKeyPerformanceIndicatorEvaluationWithData }
  | {
      kind: 'fetch';
      month: string;
      production_key_performance_indicator_id: number;
      user_id?: number | null;
      company_product_id?: number | null;
    }
  | ProductionKeyPerformanceIndicatorEvaluationWithData;

type PreviewDialogState = {
  status: 'loading' | 'ready' | 'error';
  evaluation: ProductionKeyPerformanceIndicatorEvaluationWithData | null;
  error: string | null;
};

@Component({
  selector: 'app-kpi-evaluation-preview-dialog',
  imports: [MatDialogModule, MatButtonModule, MatProgressSpinnerModule, KpiEvaluationCardComponent],
  templateUrl: './kpi-evaluation-preview-dialog.component.html',
  styleUrl: './kpi-evaluation-preview-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiEvaluationPreviewDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<KpiEvaluationPreviewDialogComponent>);
  private readonly raw_data = inject<KpiEvaluationPreviewDialogData>(MAT_DIALOG_DATA);
  private readonly api = inject(ApiService);

  private readonly fetch_state = toSignal(this.createStateObservable(), {
    initialValue: {
      status: 'loading',
      evaluation: null,
      error: null,
    } satisfies PreviewDialogState,
  });

  readonly is_loading = computed((): boolean => this.fetch_state().status === 'loading');
  readonly error_message = computed((): string | null => this.fetch_state().error);
  readonly normalized_data = computed((): ProductionKeyPerformanceIndicatorEvaluationWithData | null => this.fetch_state().evaluation);

  readonly indicator_name = computed((): string => {
    return this.normalized_data()?.production_key_performance_indicator?.name ?? 'Métrica';
  });

  close(): void {
    this.dialogRef.close();
  }

  private createStateObservable(): Observable<PreviewDialogState> {
    const fetch_params = this.resolveFetchParams();
    if (fetch_params === null) {
      return of({
        status: 'ready',
        evaluation: this.resolveInlineEvaluation(),
        error: null,
      });
    }

    let params = new HttpParams()
      .set('month', fetch_params.month)
      .set('production_key_performance_indicator_id', String(fetch_params.production_key_performance_indicator_id));

    if (fetch_params.user_id != null) {
      params = params.set('user_id', String(fetch_params.user_id));
    }
    if (fetch_params.company_product_id != null) {
      params = params.set('company_product_id', String(fetch_params.company_product_id));
    }

    return this.api.getProductionKpiEvaluationDetail(params).pipe(
      map(
        (evaluation): PreviewDialogState => ({
          status: 'ready',
          evaluation,
          error: null,
        })
      ),
      catchError(
        (): Observable<PreviewDialogState> =>
          of({
            status: 'error',
            evaluation: null,
            error: 'No se ha podido cargar el detalle de la métrica.',
          })
      ),
      startWith({
        status: 'loading',
        evaluation: null,
        error: null,
      } satisfies PreviewDialogState)
    );
  }

  private resolveFetchParams(): {
    month: string;
    production_key_performance_indicator_id: number;
    user_id?: number | null;
    company_product_id?: number | null;
  } | null {
    const data = this.raw_data;
    if (this.isFetchData(data)) {
      return data;
    }
    return null;
  }

  private resolveInlineEvaluation(): ProductionKeyPerformanceIndicatorEvaluationWithData | null {
    const data = this.raw_data;
    if (this.isFetchData(data)) {
      return null;
    }
    if (this.isWrappedData(data)) {
      return data.evaluation;
    }
    return data;
  }

  private isFetchData(data: KpiEvaluationPreviewDialogData): data is Extract<KpiEvaluationPreviewDialogData, { kind: 'fetch' }> {
    return typeof data === 'object' && data !== null && 'kind' in data && data.kind === 'fetch';
  }

  private isWrappedData(data: KpiEvaluationPreviewDialogData): data is Extract<KpiEvaluationPreviewDialogData, { kind: 'data' }> {
    return typeof data === 'object' && data !== null && 'kind' in data && data.kind === 'data';
  }
}
